import { createReducer, on } from '@ngrx/store';
import {
  AssignUserToNewGroupFailure,
  AssignUserToNewGroupRequest,
  AssignUserToNewGroupSuccess,
  ClearState,
  EditProfileByUserIdFailure,
  EditProfileByUserIdRequest,
  EditProfileByUserIdSuccess,
  FetchClinicFilterFailure,
  FetchClinicFilterRequest,
  FetchClinicFilterSuccess,
  FetchGroupFilterFailure,
  FetchGroupFilterRequest,
  FetchGroupFilterSuccess,
  FetchRolesListFailure,
  FetchRolesListRequest,
  FetchRolesListSuccess,
  FetchStatusFilterFailure,
  FetchStatusFilterRequest,
  FetchStatusFilterSuccess,
  FetchUsersListFailure,
  FetchUsersListRequest,
  FetchUsersListSuccess, UpdateUserStatusFailure, UpdateUserStatusRequest, UpdateUserStatusSuccess
} from './manage-users.actions';
import { Filter } from "../../../../models/filter";
import { UsersList } from "../../../../models/users-list";


// State for this feature (User)
export interface ManageUsersState {
  statusFilter: Filter;
  clinicFilter: Filter;
  groupFilter: Filter;
  usersList: UsersList;
  rolesList: Filter,
  editUserStatusInfo: {
    loading: boolean
  },
  assignGroupLoading: boolean,
  updatingUserStatus: boolean,
}

const initialState: ManageUsersState = {

  clinicFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  statusFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  groupFilter: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  usersList: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  rolesList: {
    list: [],
    total: null,
    pageNumber: 1,
    limit: 10,
    hasNext: false,
    hasPrev: false,
    loading: false
  },
  editUserStatusInfo: {
    loading: false
  },
  assignGroupLoading: false,
  updatingUserStatus: false,
};

export const reducer = createReducer(
  initialState,

  // CLEAR STATE
  on(ClearState, () => initialState),

  // FETCH CLINIC FILTER
  on(FetchClinicFilterRequest, (state, props) => ({
    ...state,
    clinicFilter: {
      ...state.clinicFilter,
      list: [
        ...(
          props.payload?.pageNumber ?
            props.payload?.pageNumber === 1 ? [] : [...state.clinicFilter.list] :
            []
        )
      ],
      loading: true
    }
  })),
  on(FetchClinicFilterSuccess, (state, props) => ({
    ...state,
    clinicFilter: {
      ...props.data,
      loading: false,
      list: [
        ...(props.data.pageNumber === 1 ? [
          ...props.data.list
        ] : [
          ...state.clinicFilter.list,
          ...props.data.list
        ])
      ]
    }
  })),
  on(FetchClinicFilterFailure, (state) => ({
    ...state,
    clinicFilter: {
      ...initialState.clinicFilter,
      loading: false
    }
  })),

  // FETCH STATUS FILTER
  on(FetchStatusFilterRequest, (state) => ({
    ...state,
    statusFilter: {
      ...initialState.statusFilter,
      loading: true
    }
  })),
  on(FetchStatusFilterSuccess, (state, props) => ({
    ...state,
    statusFilter: {
      ...props.statusFilter,
      loading: false
    }
  })),
  on(FetchStatusFilterFailure, (state) => ({
    ...state,
    statusFilter: {
      ...initialState.statusFilter,
      loading: false
    }
  })),

  // FETCH GROUP FILTER
  on(FetchGroupFilterRequest, (state, props) => ({
    ...state,
    groupFilter: {
      ...state.groupFilter,
      list: [
        ...(
          props.payload?.pageNumber ?
            props.payload?.pageNumber === 1 ? [] : [...state.groupFilter.list] :
            []
        )
      ],
      loading: true
    }
  })),
  on(FetchGroupFilterSuccess, (state, props) => ({
    ...state,
    groupFilter: {
      ...props.data,
      list: [
        ...(props.data.pageNumber === 1 ? [
          ...props.data.list
        ] : [
          ...state.groupFilter.list,
          ...props.data.list
        ])
      ],
      loading: false
    }
  })),
  on(FetchGroupFilterFailure, (state) => ({
    ...state,
    groupFilter: {
      ...initialState.groupFilter,
      loading: false
    }
  })),

  // FETCH USERS LIST
  on(FetchUsersListRequest, (state, props) => ({
    ...state,
    usersList: {
      ...state.usersList,
      list: [
        ...(
          props.payload?.pageNumber ?
            props.payload?.pageNumber === 1 ? [] : [...state.usersList.list] :
            []
        )
      ],
      loading: true,
    }
  })),
  on(FetchUsersListSuccess, (state, props) => ({
    ...state,
    usersList: {
      ...props.data,
      loading: false,
      list: [
        ...(props.data.pageNumber === 1 ? [
          ...props.data.list
        ] : [
          ...state.usersList.list,
          ...props.data.list
        ])
      ]
    }
  })),
  on(FetchUsersListFailure, (state) => ({
    ...state,
    usersList: {
      ...state.usersList,
      hasNext: false,
      hasPrev: false,
      loading: false
    }
  })),

  // FETCH ROLES
  on(FetchRolesListRequest, (state, props) => ({
    ...state,
    rolesList: {
      ...initialState.rolesList,
      loading: true
    },
  })),
  on(FetchRolesListSuccess, (state, props) => {
    return ({
      ...state,
      rolesList: {
        ...props.roleList,
        loading: false
      }
    })
  }),
  on(FetchRolesListFailure, (state, props) => ({
    ...state,
    rolesList: {
      ...initialState.rolesList,
      loading: false
    }
  })),

  // EDIT PROFILE BY USER ID
  on(EditProfileByUserIdRequest, (state) => ({
    ...state,
    editUserStatusInfo: {
      loading: true,
    }
  })),
  on(EditProfileByUserIdSuccess, (state, props) => ({
    ...state,
    editUserStatusInfo: {
      loading: false,
    }
  })),
  on(EditProfileByUserIdFailure, (state) => ({
    ...state,
    editUserStatusInfo: {
      loading: false,
    }
  })),

  on(AssignUserToNewGroupRequest, state => ({ ...state, assignGroupLoading: true })),

  on(AssignUserToNewGroupSuccess, state => ({ ...state, assignGroupLoading: false })),

  on(AssignUserToNewGroupFailure, state => ({ ...state, assignGroupLoading: false })),

  on(UpdateUserStatusRequest, state => ({ ...state, updatingUserStatus: true })),

  on(UpdateUserStatusSuccess, state => ({ ...state, updatingUserStatus: false })),

  on(UpdateUserStatusFailure, state => ({ ...state, updatingUserStatus: false })),
);
